import { type SignField, type SigningView, remainingFields } from "@nmt/shared";
import { useEffect, useRef, useState } from "react";
import { errorMessage, post } from "../api";
import { SignaturePad } from "./signature-pad";
import { Alert, Button, Dialog, Field, Loading, cx } from "./ui";

/** What the customer adopts at their first tap: shown in each spot as they tap it, and sent when they finish. */
export interface Adopted {
  name: string;
  initials: string;
  signaturePng: string;
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 4);

type PdfDoc = { numPages: number; getPage(n: number): Promise<PdfPage> };
type PdfPage = {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: { canvasContext: CanvasRenderingContext2D; viewport: unknown }): { promise: Promise<void> };
};

/** pdf.js, loaded only on the signing page. */
async function openPdf(url: string): Promise<PdfDoc> {
  const [pdfjs, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return (await pdfjs.getDocument({ url, isEvalSupported: false }).promise) as unknown as PdfDoc;
}

const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });

/**
 * The document as sent, page by page, with a box on each spot to initial and on the signature line.
 * Each tap is recorded by the server (with its time); "Next" jumps to the next spot left.
 */
export function GuidedSigning({
  documentUrl,
  tapUrl,
  signing,
  adopted,
  onAdopt,
  defaultName,
  consent,
  onChange,
  finish,
}: {
  documentUrl: string;
  tapUrl: string;
  signing: SigningView;
  adopted: Adopted | null;
  onAdopt: (a: Adopted) => void;
  defaultName: string;
  /** The ESIGN consent they agree to when adopting. */
  consent: string;
  onChange: (s: SigningView) => void;
  /** The finish button, shown once every spot is done. */
  finish: React.ReactNode;
}) {
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [loadError, setLoadError] = useState("");
  const [tapError, setTapError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [adopting, setAdopting] = useState<SignField | null>(null);
  const spots = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    let live = true;
    openPdf(documentUrl).then(
      (d) => live && setDoc(d),
      (e) => live && setLoadError(errorMessage(e) || "The document didn't load."),
    );
    return () => {
      live = false;
    };
  }, [documentUrl]);

  const left = remainingFields(signing.fields, signing.done);
  const initialsLeft = left.filter((f) => f.kind === "initials").length;
  const total = signing.fields.length;
  const doneCount = total - left.length;

  const tap = async (f: SignField, who = adopted) => {
    if (signing.done[f.id] || busy) return;
    if (!who) {
      setAdopting(f);
      return;
    }
    if (f.kind === "signature" && initialsLeft) {
      setTapError(`Initial ${initialsLeft === 1 ? "the last spot" : `the other ${initialsLeft} spots`} first. Tap Next to go to ${initialsLeft === 1 ? "it" : "them"}.`);
      return;
    }
    setBusy(f.id);
    setTapError("");
    try {
      const next = await post<SigningView>(tapUrl, { fieldId: f.id });
      onChange(next);
      const after = remainingFields(next.fields, next.done)[0];
      if (after) window.setTimeout(() => jump(after), 350);
    } catch (e) {
      setTapError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const jump = (f: SignField | undefined) => {
    if (!f) return;
    spots.current.get(f.id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (loadError) return <Alert>{loadError} Refresh the page to try again.</Alert>;
  return (
    <div className="flex flex-col gap-3">
      {adopting && (
        <AdoptDialog
          defaultName={adopted?.name || defaultName}
          consent={consent}
          onClose={() => setAdopting(null)}
          onAdopt={(a) => {
            const f = adopting;
            setAdopting(null);
            onAdopt(a);
            void tap(f, a);
          }}
        />
      )}
      <div className="flex flex-col gap-3 pb-24">
        {!doc ? (
          <div className="grid h-60 place-items-center rounded-[var(--radius-panel)] bg-white shadow-[var(--shadow-card)]">
            <Loading />
          </div>
        ) : (
          Array.from({ length: doc.numPages }, (_, i) => (
            <PdfPageView key={i} doc={doc} index={i}>
              {(scale, widthPts, heightPts) =>
                signing.fields
                  .filter((f) => f.page === i)
                  .map((f) => (
                    <Spot
                      key={f.id}
                      f={f}
                      scale={scale}
                      widthPts={widthPts}
                      heightPts={heightPts}
                      at={signing.done[f.id]}
                      adopted={adopted ?? { name: "", initials: "", signaturePng: "" }}
                      busy={busy === f.id}
                      next={left[0]?.id === f.id}
                      waiting={f.kind === "signature" && initialsLeft > 0}
                      onTap={() => tap(f)}
                      bind={(el) => (el ? spots.current.set(f.id, el) : spots.current.delete(f.id))}
                    />
                  ))
              }
            </PdfPageView>
          ))
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{left.length ? (initialsLeft ? `Initial here: ${initialsLeft} ${initialsLeft === 1 ? "spot" : "spots"} left` : "Tap the signature box to sign") : "All done. Finish to sign."}</div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-mist">
              <div className="h-full rounded-full bg-[var(--color-ok,#16a34a)] transition-all" style={{ width: `${total ? (doneCount / total) * 100 : 0}%` }} />
            </div>
            {tapError && <div className="mt-1 text-sm text-[var(--color-danger,#b91c1c)]">{tapError}</div>}
          </div>
          {left.length ? (
            <Button variant="primary" onClick={() => jump(left[0])}>
              {doneCount ? "Next" : "Start"}
            </Button>
          ) : adopted ? (
            finish
          ) : (
            // Came back after tapping everything (a reload): adopt again to finish.
            <Button variant="primary" onClick={() => setAdopting(signing.fields.find((f) => f.kind === "signature") ?? signing.fields[0]!)}>
              Adopt your signature
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** One page of the PDF on a canvas, sized to the screen, with its spots on top. */
function PdfPageView({ doc, index, children }: { doc: PdfDoc; index: number; children: (scale: number, widthPts: number, heightPts: number) => React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ scale: number; w: number; h: number } | null>(null);

  useEffect(() => {
    let live = true;
    const draw = async () => {
      const page = await doc.getPage(index + 1);
      const base = page.getViewport({ scale: 1 });
      const cssW = box.current?.clientWidth || base.width;
      const scale = cssW / base.width;
      const ratio = Math.min(window.devicePixelRatio || 1, 3);
      const vp = page.getViewport({ scale: scale * ratio });
      const c = canvas.current;
      if (!live || !c) return;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      await page.render({ canvasContext: c.getContext("2d")!, viewport: vp }).promise;
      if (live) setSize({ scale, w: base.width, h: base.height });
    };
    void draw();
    let width = box.current?.clientWidth ?? 0;
    const ro = new ResizeObserver(() => {
      const w = box.current?.clientWidth ?? 0;
      if (Math.abs(w - width) > 2) {
        width = w;
        void draw();
      }
    });
    if (box.current) ro.observe(box.current);
    return () => {
      live = false;
      ro.disconnect();
    };
  }, [doc, index]);

  return (
    <div ref={box} className="relative overflow-hidden rounded-[var(--radius-panel)] bg-white shadow-[var(--shadow-card)]" style={size ? { aspectRatio: `${size.w} / ${size.h}` } : { aspectRatio: "612 / 792" }}>
      <canvas ref={canvas} className="absolute inset-0 h-full w-full" aria-label={`Page ${index + 1}`} />
      {size && children(size.scale, size.w, size.h)}
    </div>
  );
}

/** A spot to tap: yellow until tapped, then the initials (or signature) with the time. */
function Spot({
  f,
  scale,
  widthPts,
  heightPts,
  at,
  adopted,
  busy,
  next,
  waiting,
  onTap,
  bind,
}: {
  f: SignField;
  scale: number;
  widthPts: number;
  heightPts: number;
  at: string | undefined;
  adopted: Adopted;
  busy: boolean;
  next: boolean;
  waiting: boolean;
  onTap: () => void;
  bind: (el: HTMLButtonElement | null) => void;
}) {
  const sig = f.kind === "signature";
  // At least a finger's size, centered on the line, kept on the page.
  const w = Math.max(f.w * scale, sig ? 150 : 64);
  const h = Math.max(f.h * scale, sig ? 48 : 30);
  const cx0 = (f.x + f.w / 2) * scale;
  const cy0 = (heightPts - f.y - f.h / 2) * scale;
  const pageW = widthPts * scale;
  const left = Math.min(Math.max(cx0 - w / 2, 2), Math.max(pageW - w - 2, 2));
  const top = Math.max(cy0 - h / 2, 2);
  return (
    <button
      ref={bind}
      type="button"
      onClick={onTap}
      aria-label={at ? `${f.label}: done at ${time(at)}` : sig ? "Sign here" : `Initial here: ${f.label}`}
      className={cx(
        "absolute flex flex-col items-center justify-center rounded-md border-2 text-center leading-tight transition",
        at
          ? "border-[#16a34a] bg-[#dcfce7]/90 text-[#14532d]"
          : waiting
            ? "border-dashed border-[#a16207] bg-[#fef9c3]/70 text-[#713f12]"
            : cx("border-[#ca8a04] bg-[#fde047]/90 text-[#422006] shadow-md", next && "animate-pulse ring-4 ring-[#fde047]/60"),
      )}
      style={{ left, top, width: w, height: h }}
    >
      {busy ? (
        <span className="text-xs font-semibold">Saving…</span>
      ) : at ? (
        sig ? (
          <>
            <img src={adopted.signaturePng} alt="Your signature" className="max-h-[70%] max-w-[95%] object-contain" />
            <span className="text-[10px]">Signed {time(at)}</span>
          </>
        ) : (
          <>
            <span className="font-serif text-sm italic">{adopted.initials}</span>
            <span className="text-[9px]">{time(at)}</span>
          </>
        )
      ) : (
        <span className="text-xs font-bold tracking-wide uppercase">{sig ? "Sign here" : "Initial"}</span>
      )}
    </button>
  );
}

/** At the first tap: their name, initials, and signature, and the consent to sign electronically. */
function AdoptDialog({ defaultName, consent, onClose, onAdopt }: { defaultName: string; consent: string; onClose: () => void; onAdopt: (a: Adopted) => void }) {
  const [name, setName] = useState(defaultName);
  const [initials, setInitials] = useState(initialsOf(defaultName));
  const [drawn, setDrawn] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const pad = useRef<HTMLCanvasElement>(null);
  const ready = agreed && drawn && name.trim().length >= 2 && initials.trim().length > 0;
  return (
    <Dialog
      title="Adopt your signature"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready} onClick={() => onAdopt({ name: name.trim(), initials: initials.trim(), signaturePng: pad.current!.toDataURL("image/png") })}>
            Adopt and initial
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">These go on each spot you tap. You'll initial every page and a few key sections, then sign at the end.</p>
        <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
          <Field label="Your full name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          <Field label="Initials" value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 8))} />
        </div>
        <SignaturePad canvas={pad} onDrawn={setDrawn} />
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-[var(--color-brand)]" />
          <span>{consent}</span>
        </label>
      </div>
    </Dialog>
  );
}
